// One day of the plan (wireframe 3). Dinner first, then lunch and breakfast;
// each dish shows its time, scaling and why ("4 at this meal + 4 as
// leftovers for Sunday's lunch"), or where its leftovers come from. Every
// meal offers "+ Add a recipe" and "+ Use leftovers" (Q18), which open the
// picker sheet (wireframes 4, 5), and an empty meal offers its likeliest
// leftovers as a one-tap suggestion.
import { useState } from "react";
import { Link } from "react-router-dom";
import { useMealPlanner } from "../../hooks/MealPlannerContext";
import { COLORS, FONT_SIZE, LAYOUT, PLANNABLE_DISH_TYPES, RECIPE_TYPE_LABELS, SPACE } from "../../lib/constants";
import { normalizeDishes } from "../../lib/planUtils";
import { budgetStatus, dishLines, nutritionStatus, recentRecipeIds, type DishLine, type Diner } from "../../lib/planView";
import { mealDate } from "../../lib/planScaling";
import { leftoverSuggestion } from "../../lib/pickerView";
import type { Day, DishType, MealSlot } from "../../lib/types";
import { smallLabel, supporting } from "../recipeStyles";
import { chip, dashedButton, inputStyle, panel, textButton } from "./planStyles";
import { PickerSheet, type PickerRequest } from "./PickerSheet";
import { PlanShareSheet } from "./PlanShareSheet";

const SLOTS: MealSlot[] = ["dinner", "lunch", "breakfast"];
const SLOT_TITLES: Record<MealSlot, string> = { dinner: "Dinner", lunch: "Lunch", breakfast: "Breakfast" };

export function DayView({ day, onBack }: { day: Day; onBack: () => void }) {
  const mp = useMealPlanner();
  const [editingBudget, setEditingBudget] = useState(false);
  const [showNutrition, setShowNutrition] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const date = mealDate(mp.weekStart, day);
  const budget = budgetStatus(mp.weekData, day, mp.recipeById);
  const nutrition = nutritionStatus(mp.weekData, day, mp.recipeById, (mp.planDefaults.householdMembers as Diner[]) || []);
  const hasMeals = SLOTS.some((s) => Object.values(normalizeDishes(mp.weekData.days[day]?.[s]) as Record<string, string>).some(Boolean));

  return (
    <div className="mp-sans" style={{ display: "flex", flexDirection: "column", gap: SPACE.x3 }}>
      <div>
        <button type="button" onClick={onBack} style={textButton}>
          ‹ Week
        </button>
        <h1 style={{ margin: 0, fontFamily: "var(--font-serif)", fontSize: FONT_SIZE.h1, fontWeight: 600, lineHeight: 1.1 }}>
          {date.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
        </h1>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: SPACE.md, alignItems: "center" }}>
        {editingBudget ? (
          <label style={{ ...supporting, display: "inline-flex", alignItems: "center", gap: SPACE.md }}>
            Time budget, minutes
            <input
              type="number"
              min="0"
              autoFocus
              defaultValue={mp.weekData.dayBudgets?.[day] ?? ""}
              onBlur={(e) => {
                const v = e.target.value;
                mp.setDayBudget(day, v === "" ? null : Math.max(0, Number(v) || 0));
                setEditingBudget(false);
              }}
              onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
              style={{ ...inputStyle, width: 90 }}
            />
          </label>
        ) : (
          <button type="button" onClick={() => setEditingBudget(true)} style={chip(budget ? (budget.over ? "caution" : "accent") : "plain")} title="Set this day's time budget">
            {budget ? budget.text : "+ Time budget"}
          </button>
        )}
        {nutrition && (
          <button type="button" aria-expanded={showNutrition} onClick={() => setShowNutrition((s) => !s)} style={chip(nutrition.over ? "caution" : "accent")}>
            {nutrition.text}
          </button>
        )}
      </div>
      {showNutrition && nutrition && (
        <div style={{ ...panel, fontSize: FONT_SIZE.sm }}>
          {nutrition.lines.map((l) => (
            <div key={l}>{l}</div>
          ))}
        </div>
      )}

      {SLOTS.map((slot) => (
        <MealSection key={slot} day={day} slot={slot} />
      ))}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: SPACE.xl, borderTop: `1px solid ${COLORS.line}`, paddingTop: SPACE.md, flexWrap: "wrap" }}>
        <button type="button" aria-expanded={sharing} onClick={() => setSharing((s) => !s)} style={{ ...textButton, fontSize: FONT_SIZE.sm }}>
          Share this day
        </button>
        {hasMeals &&
          (confirmClear ? (
            <span style={{ display: "inline-flex", gap: SPACE.xl, alignItems: "center" }}>
              <button type="button" onClick={() => (mp.clearDay(day), setConfirmClear(false))} style={{ ...textButton, color: COLORS.danger, fontSize: FONT_SIZE.sm }}>
                Clear {day}, for good
              </button>
              <button type="button" onClick={() => setConfirmClear(false)} style={{ ...textButton, color: COLORS.soft, fontSize: FONT_SIZE.sm }}>
                Keep it
              </button>
            </span>
          ) : (
            <button type="button" onClick={() => setConfirmClear(true)} style={{ ...textButton, color: COLORS.danger, fontSize: FONT_SIZE.sm }}>
              Clear day
            </button>
          ))}
      </div>
      {sharing && <PlanShareSheet initialDay={day} onClose={() => setSharing(false)} />}
    </div>
  );
}

function MealSection({ day, slot }: { day: Day; slot: MealSlot }) {
  const mp = useMealPlanner();
  const now = new Date();
  const [picker, setPicker] = useState<PickerRequest | null>(null);
  const lines = dishLines(mp.plansByWeek, mp.weekStart, day, slot, mp.recipeById, now, recentRecipeIds(mp.plansByWeek, mp.weekStart));
  const isFuture = mealDate(mp.weekStart, day) > new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dishes = normalizeDishes(mp.weekData.days[day]?.[slot]) as Partial<Record<DishType, string>>;
  // A new dish goes in as the Main when there isn't one, else the next free
  // dish type (Side, Dessert...).
  const freeTypes = PLANNABLE_DISH_TYPES.filter((dt) => !dishes[dt]);
  const nextType: DishType = !dishes.entree ? "entree" : (freeTypes[0] ?? "other");

  const suggestion = lines.length === 0 ? leftoverSuggestion(mp.plansByWeek, { weekStart: mp.weekStart, day, mealSlot: slot }, mp.recipeById, now) : null;
  // A meal starts with its Main; after that, any dish type not used yet.
  const add = (tab: PickerRequest["tab"]) => setPicker({ tab, dishType: nextType, typeChoices: dishes.entree && freeTypes.length ? freeTypes : [nextType] });

  const remove = (line: DishLine) => (line.dishType === "entree" ? mp.setDish(day, slot, "entree", "") : mp.removeDishRow(day, slot, line.dishType));

  return (
    <section aria-labelledby={`meal-${slot}`} style={{ display: "flex", flexDirection: "column", gap: SPACE.md }}>
      <h2 id={`meal-${slot}`} style={{ ...smallLabel, color: COLORS.soft, margin: 0 }}>
        {SLOT_TITLES[slot]}
      </h2>
      {lines.map((line) => (
        <div key={line.dishType} style={{ display: "flex", gap: SPACE.xl, alignItems: "flex-start", padding: "var(--space-md) 0", borderBottom: `1px solid ${COLORS.line}` }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: "block", fontSize: FONT_SIZE.xs, fontWeight: line.isLeftover ? 700 : 400, color: line.isLeftover ? COLORS.caution : COLORS.soft }}>
              {line.label}
              {line.isLeftover && line.dishType !== "entree" ? ` · ${RECIPE_TYPE_LABELS[line.dishType]}` : ""}
            </span>
            {line.recipeId ? (
              <Link to={`/recipes/${line.recipeId}`} style={{ fontFamily: "var(--font-serif)", fontSize: FONT_SIZE.h4, fontWeight: 600, color: COLORS.ink, textDecorationColor: COLORS.line, textUnderlineOffset: 3 }}>
                {line.title}
              </Link>
            ) : (
              <span style={{ fontFamily: "var(--font-serif)", fontSize: FONT_SIZE.h4, fontWeight: 600 }}>{line.title}</span>
            )}
            {line.isRepeat && <span style={{ ...supporting, color: COLORS.caution, fontWeight: 700 }}> · repeat</span>}
            {line.meta && <span style={{ ...supporting, display: "block" }}>{line.meta}</span>}
            {line.reason && <span style={{ display: "block", fontSize: FONT_SIZE.sm, color: COLORS.caution, fontWeight: 700 }}>{line.reason}</span>}
            {/* "Cooked" (slice 7c): offered from today back, and shown whenever it is set. */}
            {line.recipeId && !line.isLeftover && (line.cooked || !isFuture) && (
              <button
                type="button"
                aria-pressed={line.cooked}
                onClick={() => mp.setCooked(mp.weekStart, day, slot, line.dishType, !line.cooked)}
                style={{ ...chip(line.cooked ? "accent" : "plain"), minHeight: 32, marginTop: SPACE.xs, fontSize: FONT_SIZE.xs }}
              >
                {line.cooked ? "✓ Cooked" : "Mark cooked"}
              </button>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: SPACE.md, flexShrink: 0 }}>
            <button type="button" onClick={() => setPicker({ tab: line.isLeftover ? "leftovers" : "recipes", dishType: line.dishType, typeChoices: [line.dishType] })} style={{ ...textButton, fontSize: FONT_SIZE.sm }}>
              Change
            </button>
            <button type="button" aria-label={`Remove ${line.title} from ${slot}`} onClick={() => remove(line)} style={{ width: LAYOUT.tapTarget, height: LAYOUT.tapTarget, border: "none", background: "transparent", color: COLORS.danger, fontSize: FONT_SIZE.h3, cursor: "pointer" }}>
              ×
            </button>
          </div>
        </div>
      ))}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: SPACE.md }}>
        <button type="button" onClick={() => add("recipes")} style={dashedButton}>
          + Add a recipe
        </button>
        <button type="button" onClick={() => add("leftovers")} style={dashedButton}>
          + Use leftovers
        </button>
      </div>
      {suggestion && (
        <button
          type="button"
          onClick={() => {
            const { weekStart, day: d, mealSlot, dishType } = suggestion.candidate;
            mp.placeLeftovers(day, slot, "entree", { source: { weekStart, day: d, mealSlot, dishType } });
          }}
          aria-label={`Use leftovers of ${suggestion.title}, from ${suggestion.meal}`}
          style={{ ...dashedButton, borderStyle: "solid", borderColor: COLORS.line, justifyContent: "flex-start", gap: SPACE.md, flexWrap: "wrap", textAlign: "left", padding: "var(--space-sm) var(--space-xl)", color: COLORS.ink, fontWeight: 400 }}
        >
          <span style={{ ...smallLabel, color: COLORS.soft }}>Suggested, one tap</span>
          <span>
            <strong style={{ color: COLORS.accent }}>Leftovers: {suggestion.title}</strong>
            <span style={supporting}> · from {suggestion.meal}</span>
          </span>
        </button>
      )}
      {picker && <PickerSheet day={day} slot={slot} request={picker} onClose={() => setPicker(null)} />}
    </section>
  );
}
